import React, { useState, useEffect, useCallback } from 'react';
import { Timestamp } from 'firebase/firestore';
import Modal from '../../modal/modal';
import {
  CalendarDaysIcon,
  TagIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PhotoIcon,
  TrashIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

export interface MaintenanceRequest {
  id: string;
  listingId: string;
  issueDescription: string;
  photos: string[];
  status: 'pending' | 'in-progress' | 'completed' | 'cancelled';
  createdAt: Timestamp;
  updatedAt: Timestamp;
  category: string;
  landlordId: string;
  tenantId: string;
}

interface MaintenanceRequestCardProps {
  request: MaintenanceRequest;
  propertyTitle: string;
  userRole: 'tenant' | 'landlord';
  onStatusChange?: (requestId: string, newStatus: string) => void;
  onDelete?: (requestId: string) => void;
  isUpdating?: boolean;
  isDeleting?: boolean;
}

const MaintenanceRequestCard: React.FC<MaintenanceRequestCardProps> = ({
  request,
  propertyTitle,
  userRole,
  onStatusChange,
  onDelete,
  isUpdating = false,
  isDeleting = false
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (onStatusChange) {
      onStatusChange(request.id, e.target.value);
    }
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (onDelete) {
      onDelete(request.id);
    }
    setIsDeleteModalOpen(false);
  };

  const openImageViewer = (index: number) => setSelectedImageIndex(index);
  const closeImageViewer = () => setSelectedImageIndex(null);

  const showNextImage = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (selectedImageIndex !== null) {
        const nextIndex = (selectedImageIndex + 1) % request.photos.length;
        setSelectedImageIndex(nextIndex);
      }
    },
    [selectedImageIndex, request.photos.length]
  );

  const showPrevImage = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (selectedImageIndex !== null) {
        const prevIndex =
          (selectedImageIndex - 1 + request.photos.length) %
          request.photos.length;
        setSelectedImageIndex(prevIndex);
      }
    },
    [selectedImageIndex, request.photos.length]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedImageIndex === null) return;
      if (e.key === 'ArrowRight') showNextImage();
      else if (e.key === 'ArrowLeft') showPrevImage();
      else if (e.key === 'Escape') closeImageViewer();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex, showNextImage, showPrevImage]);

  // Design Config
  const statusConfig = {
    pending: {
      label: 'Pending',
      classes:
        'bg-yellow-50 text-yellow-700 border-yellow-200 ring-yellow-600/20'
    },
    'in-progress': {
      label: 'In Progress',
      classes: 'bg-blue-50 text-blue-700 border-blue-200 ring-blue-600/20'
    },
    completed: {
      label: 'Completed',
      classes: 'bg-green-50 text-green-700 border-green-200 ring-green-600/20'
    },
    cancelled: {
      label: 'Cancelled',
      classes: 'bg-gray-50 text-gray-600 border-gray-200 ring-gray-500/20'
    }
  };

  const currentStatus = statusConfig[request.status];

  return (
    <>
      <div
        className={`group relative flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 transition-all duration-200 hover:shadow-md ${isDeleting ? 'opacity-50 pointer-events-none' : ''}`}
      >
        {/* Header Section */}
        <div className="p-5 pb-3">
          <div className="flex justify-between items-start gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                <span className="truncate max-w-[200px]">{propertyTitle}</span>
                <span>•</span>
                <span>
                  {request.createdAt.toDate().toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric'
                  })}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 leading-tight">
                {request.issueDescription}
              </h3>
            </div>

            <div
              className={`px-2.5 py-1 rounded-full text-xs font-medium border ring-1 ring-inset whitespace-nowrap ${currentStatus.classes}`}
            >
              {currentStatus.label}
            </div>
          </div>

          <div className="mt-3 flex items-center gap-3 text-sm text-gray-600">
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-50 border border-gray-100">
              <TagIcon className="w-3.5 h-3.5 text-gray-400" />
              {request.category}
            </span>
          </div>
        </div>

        {/* Photos Grid */}
        {request.photos && request.photos.length > 0 && (
          <div className="px-5 pb-2">
            <div className="grid grid-cols-4 gap-2">
              {request.photos.slice(0, 3).map((photo, index) => (
                <button
                  key={index}
                  onClick={() => openImageViewer(index)}
                  className="relative aspect-square rounded-lg overflow-hidden ring-1 ring-gray-900/5 group/img focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <img
                    src={photo}
                    alt={`Evidence ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover/img:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/10 transition-colors" />
                </button>
              ))}

              {request.photos.length > 3 && (
                <button
                  onClick={() => openImageViewer(3)}
                  className="aspect-square rounded-lg bg-gray-50 border border-gray-100 flex flex-col items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                >
                  <span className="text-sm font-semibold">
                    +{request.photos.length - 3}
                  </span>
                  <PhotoIcon className="w-4 h-4 mt-1 opacity-50" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Footer / Actions */}
        <div className="mt-auto p-4 border-t border-gray-100 bg-gray-50/50 rounded-b-xl flex items-center justify-between gap-4">
          {/* Status Changer (Landlord Only) */}
          {userRole === 'landlord' && onStatusChange ? (
            <div className="relative flex-grow max-w-[200px]">
              {isUpdating ? (
                <div className="flex items-center text-xs text-blue-600 animate-pulse">
                  <ArrowPathIcon className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Updating...
                </div>
              ) : (
                <div className="relative">
                  <select
                    id={`status-${request.id}`}
                    value={request.status}
                    onChange={handleStatusChange}
                    className="appearance-none w-full bg-white pl-3 pr-8 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-colors cursor-pointer"
                  >
                    <option value="pending">Mark as Pending</option>
                    <option value="in-progress">Mark as In Progress</option>
                    <option value="completed">Mark as Completed</option>
                    <option value="cancelled">Mark as Cancelled</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                    <svg className="h-3 w-3 fill-current" viewBox="0 0 20 20">
                      <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Spacer for non-landlords to push delete button to right if it exists
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <CalendarDaysIcon className="w-3.5 h-3.5" />
              Updated {request.updatedAt.toDate().toLocaleDateString()}
            </div>
          )}

          {/* Delete Action (Optional) */}
          {onDelete && (
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="text-gray-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-md transition-colors disabled:opacity-50"
              title="Delete Request"
            >
              {isDeleting ? (
                <ArrowPathIcon className="w-4 h-4 animate-spin text-red-500" />
              ) : (
                <TrashIcon className="w-4 h-4" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImageIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop with blur */}
          <div
            className="absolute inset-0 bg-gray-900/90 backdrop-blur-sm transition-opacity"
            onClick={closeImageViewer}
          />

          <div
            className="relative w-full max-w-5xl h-full flex flex-col items-center justify-center pointer-events-none"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Controls */}
            <div className="absolute top-0 right-0 z-10 flex gap-2 pointer-events-auto">
              <button
                onClick={closeImageViewer}
                className="p-2 text-white/70 hover:text-white bg-black/20 hover:bg-black/40 rounded-full backdrop-blur-md transition-all"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={showPrevImage}
              className="absolute left-0 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all pointer-events-auto"
            >
              <ChevronLeftIcon className="w-8 h-8" />
            </button>

            <button
              onClick={showNextImage}
              className="absolute right-0 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all pointer-events-auto"
            >
              <ChevronRightIcon className="w-8 h-8" />
            </button>

            {/* Main Image */}
            <img
              src={request.photos[selectedImageIndex]}
              alt={`Evidence ${selectedImageIndex + 1}`}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl pointer-events-auto select-none"
            />

            {/* Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/50 backdrop-blur-md rounded-full text-white text-xs font-medium pointer-events-auto">
              {selectedImageIndex + 1} / {request.photos.length}
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <div className="text-center">
          <TrashIcon className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Delete Request
          </h3>
          <p className="text-sm text-gray-500 mb-6">
            Are you sure you want to delete this maintenance request? This
            action cannot be undone.
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default MaintenanceRequestCard;
